// @ts-nocheck
import React, { useEffect, useState, useRef } from 'react'
// import _ from 'lodash'
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx'
import JsPDF from 'jspdf'
import 'jspdf-autotable'
import { PlusIcon, PencilSquareIcon, TrashIcon, QrCodeIcon, DocumentArrowDownIcon, DocumentArrowUpIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

import { useTranslation } from 'react-i18next'
import { useAuth } from 'auth/AuthContext'
import { parseDate } from 'utils'
import { makeRequest } from 'services/request.service'
import { showAlert } from 'services/alerts.service'
import PrintModal from 'components/modals/PrintModal'
import QRModal from 'components/modals/QRModal'
import WorkerModal from 'components/modals/WorkerModal'
import ButtonComponent from 'components/common/ButtonComponent'
import ActionCardComponent from 'components/common/ActionCardComponent'
import TableComponent from 'components/common/TableComponent'
import UploadWorkersModal from 'components/modals/UploadWorkersModal'
import PageLoadingState from 'components/common/PageLoadingState'
import ModalComponent from 'components/common/ModalComponent'

const WorkersManagement = () => {
  const hasFetched = useRef(false)
  const { user } = useAuth()
  const { t } = useTranslation()

  const [state, setState] = useState({
    searchTerm: '',
    workers: [],
    selectedWorker: null,
    selectedWorkerForQR: null,
    totals: {
      totalWorkers: 0,
      totalActive: 0,
      totalInactive: 0
    },
    isWorkerModalOpen: false,
    isQRModalOpen: false,
    isPrintModalOpen: false,
    isOpenWorkersModal: false,
    loading: true,
    showDeleteConfirm: false,
    pendingDelete: null
  })

  const getWorkers = async () => {
    // uncomment when backend returns last entity created
    // if (hasFetched.current) return
    // hasFetched.current = true

    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/${user.farmId}/all`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response) {
      setState((s) => ({
        ...s,
        workers: response.data
      }))
    }
  }

  const getTotals = async () => {
    const response = await makeRequest({
      method: 'get',
      url: `/management/workers-management/totals/${user.farmId}`,
      headers: { Authorization: `Bearer ${localStorage.getItem('secret')}` }
    })

    if (response) {
      setState((s) => ({
        ...s,
        totals: response.data
      }))
    }
  }

  const openAddWorkerModal = () => {
    setState((s) => ({
      ...s,
      selectedWorker: {},
      isWorkerModalOpen: true
    }))
  }

  const openModalForEdit = (worker) => {
    setState((s) => ({
      ...s,
      selectedWorker: worker,
      isWorkerModalOpen: true
    }))
  }

  const closeModal = () => {
    setState((s) => ({
      ...s,
      selectedWorker: null,
      isWorkerModalOpen: false
    }))
  }

  const handleSave = async (workerData) => {
    const isEditing = Object.keys(state.selectedWorker).length > 0

    const response = await makeRequest({
      method: isEditing ? 'put' : 'post',
      url: isEditing
        ? `/management/workers-management/${workerData.workerId}`
        : '/management/workers-management',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`
      },
      data: {
        farmId: user.farmId,
        ...workerData
      },
      alertResult: true
    })

    if (response.status === 200) {
      closeModal()
      getWorkers()
      getTotals()
    }
  }

  const handleDelete = (worker) => {
    setState(s => ({ ...s, showDeleteConfirm: true, pendingDelete: worker }))
  }

  const confirmDelete = async () => {
    const worker = state.pendingDelete
    setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))
    const response = await makeRequest({
      method: 'patch',
      url: `/management/workers-management/delete-worker/${worker.workerId}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('secret')}`,
        'Content-Type': 'application/json'
      },
      data: {},
      alertResult: true
    })

    if (response.status === 200) {
      getWorkers()
      getTotals()
    }
  }

  const handleQRClick = (worker) => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isQRModalOpen: true
    }))
  }

  const handlePrintClick = (worker) => {
    setState(s => ({
      ...s,
      selectedWorkerForQR: worker,
      isPrintModalOpen: true
    }))
  }

  const handleExportExcel = () => {
    try {
      const dataToExport = state.workers.map(worker => ({
        ID: worker.workerId,
        Name: worker.firstName,
        'Last Name': worker.lastName,
        Status: worker.isActive ? 'Active' : 'Suspended',
        'Created At': worker.createdAt
      }))

      const worksheet = XLSXUtils.json_to_sheet(dataToExport)
      const workbook = XLSXUtils.book_new()
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Workers')

      // Ajustar el ancho de las columnas
      const maxWidth = dataToExport.reduce((w, r) => Math.max(w, r.Name.length), 10)
      worksheet['!cols'] = [
        { wch: 10 }, // ID
        { wch: maxWidth }, // Name
        { wch: maxWidth }, // Last Name
        { wch: 15 }, // Status
        { wch: 15 }, // Phone
        { wch: 25 }, // Email
        { wch: 15 }, // Role
        { wch: 20 } // Created At
      ]

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      })

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })

      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `workers_${new Date().toISOString().split('T')[0]}.xlsx`

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(url)

      showAlert({
        text: t('mgmt.common.excelDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      showAlert({
        text: t('mgmt.common.excelFailed'),
        icon: 'error'
      })
    }
  }

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF()

      doc.setFontSize(16)
      doc.text(t('mgmt.workers.reportTitle'), 14, 15)
      doc.setFontSize(10)
      doc.text(t('mgmt.workers.generatedOn', { date: new Date().toLocaleDateString() }), 14, 25)

      const tableColumn = [
        'ID',
        'Name',
        'Last Name',
        'Status',
        'Phone',
        'Email',
        'Role',
        'Created At'
      ]

      const tableRows = state.workers.map(worker => [
        worker.workerId,
        worker.firstName,
        worker.lastName,
        worker.isActive ? 'Active' : 'Suspended',
        '',
        '',
        worker.createdAt
      ])

      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      })

      doc.save(`workers_${new Date().toISOString().split('T')[0]}.pdf`)

      showAlert({
        text: t('mgmt.common.pdfDownloaded'),
        icon: 'success'
      })
    } catch (error) {
      console.error('Error exporting to PDF:', error)
      showAlert({
        text: t('mgmt.common.pdfFailed'),
        icon: 'error'
      })
    }
  }

  const filteredWorkers = state.workers.filter(worker => {
    const fullName = `${worker?.firstName ?? ''} ${worker?.lastName ?? ''}`.toLowerCase()
    const oldId = worker.oldId ?? ''
    const status = worker.isActive ? 'active' : 'suspended'

    return (
      fullName.includes(state.searchTerm) ||
      status.includes(state.searchTerm) ||
      oldId.includes(state.searchTerm)
    )
  })

  const rowsFiltered = filteredWorkers.map(worker => ({
    ...worker,
    status: worker.isActive ? 'Active' : 'Suspended',
    fullName: `${worker.firstName} ${worker.lastName}`
  }))

  const workersTableData = {
    columns: [
      {
        key: 'workerId',
        labelKey: 'mgmt.workers.cols.number'
      },
      {
        key: 'oldId',
        labelKey: 'mgmt.workers.cols.pickId'
      },
      {
        key: 'fullName',
        labelKey: 'mgmt.workers.cols.fullName'
      },
      {
        key: 'status',
        labelKey: 'mgmt.workers.cols.status',
        cell: status => (
          <span className={status === 'Active' ? 'text-green-600' : 'text-red-600'}>
            {t(status === 'Active' ? 'mgmt.workers.active' : 'mgmt.workers.suspended')}
          </span>
        )
      },
      {
        key: 'createdAt',
        labelKey: 'mgmt.workers.cols.creationDate',
        cell: createdAt => parseDate(createdAt)
      },
      {
        key: 'actions',
        labelKey: 'mgmt.workers.cols.manage',
        cell: (value, worker) => (
          <div className='flex items-center gap-2'>
            <button onClick={() => handleQRClick(worker)} title={t('common.qrCode')} className='p-1 rounded transition'>
              <QrCodeIcon className='w-5 h-5 text-gray-600' />
            </button>
            <button onClick={() => openModalForEdit(worker)} title={t('common.edit')} className='p-1 rounded transition'>
              <PencilSquareIcon className='w-5 h-5 text-blue-500' />
            </button>
            <button onClick={() => handleDelete(worker)} title={t('common.delete')} className='p-1 rounded transition'>
              <TrashIcon className='w-5 h-5 text-red-500' />
            </button>
          </div>
        )
      }
    ],
    rows: rowsFiltered
  }

  const onToggleUploadCsvModal = () => {
    setState(s => ({
      ...s,
      isOpenWorkersModal: !s.isOpenWorkersModal
    }))
  }

  useEffect(() => {
    if (!user?.farmId) return
    if (hasFetched.current) return
    hasFetched.current = true

    Promise.all([
      getWorkers(),
      getTotals()
    ]).finally(() => {
      setState(s => ({ ...s, loading: false }))
    })
  }, [user?.farmId])

  if (state.loading) {
    return <PageLoadingState message={t('mgmt.workers.loading')} rows={8} />
  }

  return (
    <>
      <div className='grid grid-cols-3 gap-6'>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalWorkers}</h2>
          <p>{t('mgmt.workers.totalWorkers')}</p>
        </div>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalActive}</h2>
          <p>{t('mgmt.workers.activeWorkers')}</p>
        </div>
        <div className='mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50 text-center'>
          <h2 className='text-xl font-bold text-green-700 mb-2'>{state.totals.totalInactive}</h2>
          <p>{t('mgmt.workers.suspendedWorkers')}</p>
        </div>
      </div>

      <ActionCardComponent>
        <input
          type='text'
          value={state.searchTerm}
          placeholder={t('mgmt.workers.filter')}
          onChange={(e) => setState(s => ({ ...s, searchTerm: e.target.value.toLowerCase() }))}
          className='search-input'
          data-align='left'
        />
        <ButtonComponent onClick={() => setState(s => ({ ...s, isOpenWorkersModal: true }))}>
          <DocumentArrowUpIcon className='w-5 h-5' />
          <span className='font-medium'>{t('mgmt.workers.import')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={openAddWorkerModal}>
          <PlusIcon className='w-5 h-5 mr-1' />
          {t('mgmt.workers.new')}
        </ButtonComponent>
        <ButtonComponent onClick={handlePrintClick}>
          <QrCodeIcon className='w-5 h-5' />
          {t('mgmt.workers.printCards')}
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel} className='flex items-center gap-2'>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('common.exportExcel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF} className='flex items-center gap-2'>
          <DocumentArrowDownIcon className='w-5 h-5' />
          <span>{t('mgmt.common.exportPdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent tableData={workersTableData} />

      <WorkerModal
        isOpen={state.isWorkerModalOpen}
        onClose={() => setState(s => ({ ...s, isWorkerModalOpen: false }))}
        worker={state.selectedWorker}
        onSave={handleSave}
      />
      <PrintModal
        isOpen={state.isPrintModalOpen}
        onClose={() => setState(s => ({ ...s, isPrintModalOpen: false }))}
        workers={state.workers}
      />
      <QRModal
        isOpen={state.isQRModalOpen}
        onClose={() => setState(s => ({ ...s, isQRModalOpen: false }))}
        worker={state.selectedWorkerForQR}
      />
      <UploadWorkersModal
        isOpen={state.isOpenWorkersModal}
        onClose={onToggleUploadCsvModal}
      />

      <ModalComponent
        isOpen={state.showDeleteConfirm}
        onClose={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
        title={t('mgmt.workers.deleteTitle')}
        maxWidth='sm'
        hideDefaultFooter
      >
        <div className='flex flex-col items-center gap-4 py-2'>
          <ExclamationTriangleIcon className='w-12 h-12 text-red-400' />
          <p className='text-sm text-gray-700 text-center'>
            {t('mgmt.workers.removeConfirm', { name: `${state.pendingDelete?.firstName ?? ''} ${state.pendingDelete?.lastName ?? ''}`.trim() })}
            <br />
            <span className='text-gray-500'>{t('mgmt.common.confirmDelete')}</span>
          </p>
          <div className='flex justify-end gap-3 w-full mt-2'>
            <button
              type='button'
              onClick={() => setState(s => ({ ...s, showDeleteConfirm: false, pendingDelete: null }))}
              className='px-4 py-2 rounded bg-gray-100 text-greenPrimary hover:bg-gray-200 transition'
            >
              {t('mgmt.common.cancel')}
            </button>
            <button
              type='button'
              onClick={confirmDelete}
              className='px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600 transition'
            >
              {t('mgmt.common.delete')}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  )
}

export default WorkersManagement
